import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import * as Titles from './Titles'
import * as Contents from './Contents'
import * as FooterDetails from './Footer'

import { StyledButton } from './atoms'
import {
  PAYMENT_TYPES,
  PAYMENT_TYPES_BY_ID,
} from 'portal-modules/payments/constants/types'
import {
  PLAID_ITEM_FATAL_ERROR_TYPES,
  PLAID_ITEM_FATAL_ERROR_CODES,
  PLAID_RELINK_BANNER_DAYS,
} from 'portal-modules/common/constants/plaid'
import {
  StyledReason,
  StyledAutoPaymentActionCard,
} from 'portal-modules/common/components/atoms'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
import isPast from 'date-fns/isPast'
import styled from '@emotion/styled'

const AutoPaymentCard = props => {
  const getTitle = () => {
    switch (props.paymentType) {
      case 'split':
        return <Titles.Split {...props} />
      case 'bimonthly':
        return <Titles.BiMonthly />
      default:
        return <Titles.AutoPayment {...props} />
    }
  }
  const { isRP } = getProjectConditions()
  const getContents = () => {
    let strPlaidReasonTxt = null
    const {
      is_approved,
      payment_type_id,
      bank_info,
      methods,
      customer_payment_account_id,
      isAddAutoPaymentAllowed,
      isEditAutoPaymentAllowed,
      isDeleteAutoPaymentAllowed,
    } = props

    if (
      payment_type_id &&
      PAYMENT_TYPES.ECHECK.value == payment_type_id &&
      bank_info
    ) {
      const { plaid_item_error } = bank_info
      const paymentAct =
        methods.length &&
        Object.values(methods).filter(
          ({ id }) => id == customer_payment_account_id,
        )
      var date = new Date()
      date.setDate(date.getDate() + PLAID_RELINK_BANNER_DAYS)

      if (
        paymentAct?.length &&
        paymentAct[0]?.bank_info &&
        paymentAct[0]?.bank_info?.plaid_verification_status !=
          'account_deleted_or_obsolete' &&
        paymentAct[0]?.bank_info?.plaid_verification_status !=
          'verification_expired' &&
        paymentAct[0]?.bank_info?.plaid_consent_expiration_time &&
        (isPast(paymentAct[0].bank_info.plaid_consent_expiration_time) ||
          i18nUtils.datetime(
            paymentAct[0].bank_info.plaid_consent_expiration_time,
            'DATETIME_ISO',
          ) < i18nUtils.datetime(date.toISOString(), 'DATETIME_ISO'))
      ) {
        strPlaidReasonTxt = __('Account needs to be relinked')
      } else if (
        plaid_item_error &&
        plaid_item_error.error_type &&
        plaid_item_error.error_code
      ) {
        strPlaidReasonTxt =
          PLAID_ITEM_FATAL_ERROR_TYPES.includes(plaid_item_error.error_type) &&
          PLAID_ITEM_FATAL_ERROR_CODES.includes(plaid_item_error.error_code)
            ? __('This account cannot be verified')
            : null
      }
    }

    switch (props.paymentType) {
      case 'split':
        return (
          <Fragment>
            <Contents.Split {...props} />
            <FooterDetails.Split {...props} />
            {!is_approved ? (
              <span>
                {isRP && isAddAutoPaymentAllowed ? (
                  <StyledButton
                    slim
                    appearance='primary'
                    onClick={handleAcceptTerms}
                    fullWidth
                  >
                    {__('Accept Terms')}
                  </StyledButton>
                ) : (
                  <></>
                )}
                {isRP && isEditAutoPaymentAllowed ? (
                  <StyledButton
                    slim
                    appearance='secondary'
                    fullWidth
                    onClick={handleEdit}
                  >
                    {__('Edit Proposed Terms')}
                  </StyledButton>
                ) : (
                  <></>
                )}
              </span>
            ) : (
              <></>
            )}
            {strPlaidReasonTxt && (
              <StyledReason>{strPlaidReasonTxt}</StyledReason>
            )}
          </Fragment>
        )
      case 'bimonthly':
        return (
          <Fragment>
            <Contents.BiMonthly {...props} />
            <FooterDetails.BiMonthly {...props} />
            {strPlaidReasonTxt && (
              <StyledReason>{strPlaidReasonTxt}</StyledReason>
            )}
          </Fragment>
        )
      default:
        return (
          <Fragment>
            <Contents.AutoPayment {...props} />
            <FooterDetails.AutoPayment {...props} />
            {strPlaidReasonTxt && (
              <StyledReason>{strPlaidReasonTxt}</StyledReason>
            )}
          </Fragment>
        )
    }
  }

  const handleEdit = () => {
    props.onEdit(props.id)
  }

  const handleDelete = () => {
    props.onDelete(props.id, props.ondeleteCallBack)
  }

  const handleAcceptTerms = () => {
    props.onAcceptTerms(props.id)
  }

  let {
    id,
    paymentType,
    is_approved,
    onDelete,
    roommate_payments,
    onAcceptTerms,
    payment_type_id,
    showEdit,
    showDelete,
    updateAllowed,
    isEditAutoPaymentAllowed,
    isDeleteAutoPaymentAllowed,
    ...rest
  } = props
  showDelete = isRP ? showDelete : true
  const cardType = PAYMENT_TYPES[PAYMENT_TYPES_BY_ID[payment_type_id]]?.getName()

  const canEdit =
    cardType == 'SEPA' || !updateAllowed
      ? false
      : showEdit && (paymentType != 'split' ? true : !!is_approved)
  const isDisabled =
    isRP && !isEditAutoPaymentAllowed && !isDeleteAutoPaymentAllowed
  return (
    <StyledAutoPaymentActionCard
      {...rest}
      id={id}
      confirmationText={__('Do you want to delete this payment?')}
      onDelete={!!showDelete && !!updateAllowed && handleDelete}
      onEdit={!!canEdit && handleEdit}
      title={getTitle()}
      isAutoPayment={true}
      isEditAutoPaymentAllowed={!!isEditAutoPaymentAllowed}
      isDeleteAutoPaymentAllowed={!!isDeleteAutoPaymentAllowed}
      disabled={isDisabled}
    >
      {getContents()}
    </StyledAutoPaymentActionCard>
  )
}

AutoPaymentCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  is_approved: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onAcceptTerms: PropTypes.func,
  paymentType: PropTypes.string.isRequired,
  payment_type_id: PropTypes.number,
  roommate_payments: PropTypes.arrayOf(PropTypes.object),
  showEdit: PropTypes.bool,
  methods: PropTypes.arrayOf(PropTypes.object),
  updateAllowed: PropTypes.bool,
  ondeleteCallBack: PropTypes.func.isRequired,
  isAddAutoPaymentAllowed: PropTypes.bool,
  isEditAutoPaymentAllowed: PropTypes.bool,
  isDeleteAutoPaymentAllowed: PropTypes.bool,
}

AutoPaymentCard.defaultProps = {
  showEdit: true,
  updateAllowed: true,
}

export default AutoPaymentCard
